// utils/pdf.js
// Generazione PDF lato client per l'app NC Elettrica.
// Usa jsPDF v4 e jspdf-autotable v5.
// NB: in jspdf-autotable v5 autoTable è una funzione: autoTable(doc, {...}),
//     non più un metodo doc.autoTable(...).

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ---------------------------------------------------------------------------
// Costanti di brand NC Elettrica
// ---------------------------------------------------------------------------
const ROSSO = [253, 0, 0]; // #fd0000 colore principale del brand
const GRIGIO_SCURO = [60, 60, 60];
const GRIGIO_CHIARO = [240, 240, 240];
const NERO = [0, 0, 0];

// Margini di pagina in mm (A4 verticale = 210 x 297 mm)
const MARGINE = 14;
const LARGHEZZA_PAGINA = 210;
const ALTEZZA_PAGINA = 297;
const LARGHEZZA_UTILE = LARGHEZZA_PAGINA - MARGINE * 2;

// ---------------------------------------------------------------------------
// Helper generici
// ---------------------------------------------------------------------------

/**
 * Formatta un numero decimale di ore in stringa "Xh Ym".
 * Esempi: 4.5 -> "4h 30m", 1 -> "1h 0m", 0 -> "0h 0m".
 * Gestisce null/undefined/NaN restituendo "0h 0m".
 */
function formatOre(n) {
  const valore = Number(n);
  if (!isFinite(valore) || isNaN(valore)) {
    return '0h 0m';
  }
  const negativo = valore < 0;
  const assoluto = Math.abs(valore);
  let ore = Math.floor(assoluto);
  // Arrotondo i minuti per evitare derive di virgola mobile (es. 4.4999 -> 30m)
  let minuti = Math.round((assoluto - ore) * 60);
  // Gestisco il caso in cui l'arrotondamento porti i minuti a 60
  if (minuti === 60) {
    ore += 1;
    minuti = 0;
  }
  return `${negativo ? '-' : ''}${ore}h ${minuti}m`;
}

/**
 * Converte una data "YYYY-MM-DD" (o stringa data) nel formato italiano gg/mm/aaaa.
 * Se il valore è mancante o non valido restituisce "-".
 */
function formatData(data) {
  if (!data) return '-';
  // Caso tipico dal backend: "YYYY-MM-DD" (eventualmente con orario dopo lo spazio o la T)
  const soloData = String(data).split('T')[0].split(' ')[0];
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(soloData);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  // Fallback: provo con Date
  const d = new Date(data);
  if (!isNaN(d.getTime())) {
    const gg = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const aaaa = d.getFullYear();
    return `${gg}/${mm}/${aaaa}`;
  }
  return String(data);
}

/**
 * Taglia i secondi da un orario "HH:MM:SS" restituendo "HH:MM".
 * Gestisce null/undefined restituendo "-".
 */
function formatOra(ora) {
  if (!ora) return '-';
  const parti = String(ora).split(':');
  if (parti.length >= 2) {
    return `${parti[0]}:${parti[1]}`;
  }
  return String(ora);
}

/**
 * Formatta una durata in minuti come "45 min" oppure "1h 30m".
 * Restituisce null se il valore non è utilizzabile.
 */
function formatMinuti(minuti) {
  if (minuti === null || minuti === undefined) return null;
  const v = Number(minuti);
  // Lo zero è un valore reale (partenza e arrivo timbrati nello stesso minuto).
  if (!isFinite(v) || isNaN(v) || v < 0) return null;
  const arrotondati = Math.round(v);
  if (arrotondati < 60) return `${arrotondati} min`;
  const ore = Math.floor(arrotondati / 60);
  const resto = arrotondati % 60;
  return resto === 0 ? `${ore}h` : `${ore}h ${resto}m`;
}

/**
 * Minuti trascorsi fra due orari "HH:MM[:SS]" dello stesso giorno.
 * Gestisce il passaggio della mezzanotte (turni notturni) e restituisce null
 * se uno dei due orari manca o non è interpretabile.
 */
function minutiTra(oraInizio, oraFine) {
  const aMinuti = (ora) => {
    if (!ora) return null;
    const p = String(ora).split(':');
    if (p.length < 2) return null;
    const h = Number(p[0]);
    const m = Number(p[1]);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };
  const a = aMinuti(oraInizio);
  const b = aMinuti(oraFine);
  if (a === null || b === null) return null;
  let delta = b - a;
  if (delta < 0) delta += 24 * 60; // oltre la mezzanotte
  return delta;
}

/**
 * Capitalizza la tipologia (manutenzione/impianto) per la visualizzazione.
 * Restituisce "-" se mancante.
 */
function formatTipologia(tipologia) {
  if (!tipologia) return '-';
  const t = String(tipologia);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Sanifica una stringa per usarla come nome file:
 * sostituisce caratteri non alfanumerici con underscore.
 */
function sanitizeNomeFile(nome) {
  if (!nome) return 'documento';
  return String(nome)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // rimuove gli accenti
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/^_+|_+$/g, '') // toglie underscore iniziali/finali
    .substring(0, 80) || 'documento';
}

/**
 * Restituisce in modo sicuro un valore stringa, con fallback su "-".
 */
function safe(valore) {
  if (valore === null || valore === undefined || valore === '') return '-';
  return String(valore);
}

/**
 * Disegna l'intestazione di brand "NC ELETTRICA" in alto a sinistra,
 * con sottotitolo, e (opzionale) un testo allineato a destra (es. la data).
 * Restituisce la coordinata Y sotto cui continuare a scrivere.
 */
function disegnaIntestazione(doc, sottotitolo, testoDestra) {
  // Barra rossa decorativa in alto
  doc.setFillColor(...ROSSO);
  doc.rect(0, 0, LARGHEZZA_PAGINA, 4, 'F');

  // Titolo brand
  doc.setTextColor(...ROSSO);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.text('NC ELETTRICA', MARGINE, 20);

  // Sottotitolo
  doc.setTextColor(...GRIGIO_SCURO);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.text(safe(sottotitolo), MARGINE, 28);

  // Testo a destra (es. data)
  if (testoDestra) {
    doc.setTextColor(...NERO);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(String(testoDestra), LARGHEZZA_PAGINA - MARGINE, 20, {
      align: 'right',
    });
  }

  // Linea separatrice
  doc.setDrawColor(...ROSSO);
  doc.setLineWidth(0.5);
  doc.line(MARGINE, 32, LARGHEZZA_PAGINA - MARGINE, 32);

  return 40; // Y di partenza per il contenuto
}

/**
 * Scrive un titolo di sezione in rosso e restituisce la Y successiva.
 */
function titoloSezione(doc, testo, y) {
  doc.setTextColor(...ROSSO);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(String(testo), MARGINE, y);
  return y + 6;
}

/**
 * Scrive un blocco di testo libero con wrapping (titolo + corpo).
 * Gestisce automaticamente il salto pagina se il testo è troppo lungo.
 * Restituisce la Y successiva.
 */
function sezioneTesto(doc, titolo, testo, y) {
  if (!testo || String(testo).trim() === '') return y;

  let yCorrente = controllaSpazio(doc, y, 16);
  yCorrente = titoloSezione(doc, titolo, yCorrente);

  doc.setTextColor(...NERO);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  const righe = doc.splitTextToSize(String(testo), LARGHEZZA_UTILE);
  const altezzaRiga = 5;

  for (let i = 0; i < righe.length; i++) {
    yCorrente = controllaSpazio(doc, yCorrente, altezzaRiga);
    doc.text(righe[i], MARGINE, yCorrente);
    yCorrente += altezzaRiga;
  }

  return yCorrente + 4;
}

/**
 * Controlla se c'è abbastanza spazio verticale prima del footer;
 * se non c'è, aggiunge una nuova pagina e restituisce la Y di inizio pagina.
 */
function controllaSpazio(doc, y, altezzaRichiesta) {
  const limiteInferiore = ALTEZZA_PAGINA - 20; // margine per il footer
  if (y + altezzaRichiesta > limiteInferiore) {
    doc.addPage();
    return MARGINE + 6;
  }
  return y;
}

/**
 * Aggiunge il footer (numero pagina + eventuale data di generazione) a TUTTE
 * le pagine del documento. Va chiamato alla fine, prima del salvataggio.
 */
function aggiungiFooter(doc, conDataGenerazione) {
  const totalePagine = doc.internal.getNumberOfPages();
  const dataGen = formatData(new Date());

  for (let i = 1; i <= totalePagine; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRIGIO_SCURO);

    // Linea sopra il footer
    doc.setDrawColor(...GRIGIO_CHIARO);
    doc.setLineWidth(0.3);
    doc.line(MARGINE, ALTEZZA_PAGINA - 14, LARGHEZZA_PAGINA - MARGINE, ALTEZZA_PAGINA - 14);

    // Numero pagina (a destra)
    doc.text(
      `Pagina ${i} di ${totalePagine}`,
      LARGHEZZA_PAGINA - MARGINE,
      ALTEZZA_PAGINA - 9,
      { align: 'right' }
    );

    // Brand a sinistra
    doc.text('NC Elettrica', MARGINE, ALTEZZA_PAGINA - 9);

    // Data di generazione al centro (solo se richiesto)
    if (conDataGenerazione) {
      doc.text(
        `Generato il ${dataGen}`,
        LARGHEZZA_PAGINA / 2,
        ALTEZZA_PAGINA - 9,
        { align: 'center' }
      );
    }
  }
}

/**
 * Opzioni di stile comuni per tutte le tabelle autoTable (look NC Elettrica).
 */
function opzioniTabella(extra) {
  return {
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 2,
      textColor: NERO,
      lineColor: [220, 220, 220],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: ROSSO,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    alternateRowStyles: {
      fillColor: GRIGIO_CHIARO,
    },
    margin: { left: MARGINE, right: MARGINE },
    ...extra,
  };
}

/**
 * Restituisce la Y subito sotto l'ultima tabella disegnata da autoTable.
 * Fallback robusto se lastAutoTable non è disponibile.
 */
function yDopoTabella(doc, yPrecedente) {
  if (doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') {
    return doc.lastAutoTable.finalY + 8;
  }
  return yPrecedente + 8;
}

// ---------------------------------------------------------------------------
// 1) PDF di UN rapportino
// ---------------------------------------------------------------------------

/**
 * Genera e scarica il PDF (A4 verticale) di un singolo rapportino.
 * @param {Object} rapportino - dati del rapportino dal backend.
 */
export function generaRapportinoPDF(rapportino) {
  // Difesa: se non arriva nulla, esco senza esplodere
  const r = rapportino || {};

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // --- Intestazione ---
  const dataRapportino = r.data || (r.intervento && r.intervento.data) || null;
  let y = disegnaIntestazione(doc, 'Rapportino di Lavoro', formatData(dataRapportino));

  // --- Riquadro info (Cliente, Cantiere, Tipologia, Compilato da) ---
  const tipologia =
    r.tipologia || (r.intervento && r.intervento.tipologia) || null;
  const cliente = r.cliente_nome || (r.intervento && r.intervento.cliente_nome);
  const cantiere = r.cantiere_nome || (r.intervento && r.intervento.cantiere_nome);
  const commessa = r.cantiere_codice || (r.intervento && r.intervento.cantiere_codice);

  const righeInfo = [['Cliente', safe(cliente)]];
  // Codice commessa del cantiere: c'era nel database ma non usciva dall'API
  if (commessa) righeInfo.push(['Commessa', safe(commessa)]);
  righeInfo.push(['Cantiere', safe(cantiere)]);
  righeInfo.push(['Tipologia', formatTipologia(tipologia)]);
  righeInfo.push(['Compilato da', safe(r.creatore_nome)]);

  autoTable(
    doc,
    opzioniTabella({
      startY: y,
      body: righeInfo,
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45, fillColor: GRIGIO_CHIARO },
        1: { cellWidth: 'auto' },
      },
      // Questa tabella non ha header
      showHead: 'never',
    })
  );
  y = yDopoTabella(doc, y);

  // --- Tabella Orari (giornata in cantiere) ---
  // Ordine cronologico reale, PAUSA COMPRESA: prima la pausa non compariva, così le
  // ore totali sembravano non tornare rispetto a inizio/fine lavoro.
  const minutiPausa =
    r.minuti_pausa != null
      ? Number(r.minuti_pausa)
      : minutiTra(r.ora_pausa_inizio, r.ora_pausa_fine);
  const pausaTesto = formatMinuti(minutiPausa);

  const righeOrari = [];
  [
    ['Arrivo cantiere', r.ora_arrivo_cantiere],
    ['Inizio lavoro', r.ora_inizio_lavoro],
    ['Inizio pausa', r.ora_pausa_inizio],
    ['Fine pausa', r.ora_pausa_fine],
    ['Fine lavoro', r.ora_fine_lavoro],
  ].forEach(([etichetta, valore]) => {
    if (valore) righeOrari.push([etichetta, formatOra(valore)]);
  });

  // Righe di riepilogo: la pausa spiega la differenza fra presenza e ore lavorate
  if (pausaTesto) {
    righeOrari.push(['Pausa (non conteggiata)', pausaTesto]);
  }
  righeOrari.push(['Ore lavorate totali', formatOre(r.ore_lavorate)]);
  const indiceTotale = righeOrari.length - 1;
  const indicePausa = pausaTesto ? righeOrari.length - 2 : -1;

  y = controllaSpazio(doc, y, 20);
  y = titoloSezione(doc, 'Orari in cantiere', y);
  autoTable(
    doc,
    opzioniTabella({
      startY: y,
      head: [['Fase', 'Orario']],
      body: righeOrari,
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 40 },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (data.row.index === indiceTotale) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [255, 230, 230];
        } else if (data.row.index === indicePausa) {
          data.cell.styles.fontStyle = 'italic';
          data.cell.styles.fillColor = [245, 245, 245];
        }
      },
    })
  );
  y = yDopoTabella(doc, y);

  // --- Tabella Viaggio ---
  // I tempi di viaggio erano già sul rapportino ma il PDF non li stampava mai.
  // Dove ci sono le timbrature uso la durata REALE, altrimenti la stima salvata.
  const durataAndataReale = minutiTra(r.ora_partenza_sede, r.ora_arrivo_cantiere);
  const durataRitornoReale = minutiTra(r.ora_partenza_cantiere, r.ora_arrivo_sede);
  const durataAndata = durataAndataReale != null ? durataAndataReale : r.tempo_viaggio_andata;
  const durataRitorno = durataRitornoReale != null ? durataRitornoReale : r.tempo_viaggio_ritorno;

  const righeViaggio = [];
  if (r.ora_partenza_sede || r.ora_arrivo_cantiere || durataAndata) {
    righeViaggio.push([
      'Andata (sede-cantiere)',
      formatOra(r.ora_partenza_sede),
      formatOra(r.ora_arrivo_cantiere),
      formatMinuti(durataAndata) || '-',
      // l'origine ha senso solo se una durata c'è davvero
      durataAndata == null ? '' : durataAndataReale != null ? 'da timbrature' : 'stimato',
    ]);
  }
  if (r.ora_partenza_cantiere || r.ora_arrivo_sede || durataRitorno) {
    righeViaggio.push([
      'Ritorno (cantiere-sede)',
      formatOra(r.ora_partenza_cantiere),
      formatOra(r.ora_arrivo_sede),
      formatMinuti(durataRitorno) || '-',
      durataRitorno == null ? '' : durataRitornoReale != null ? 'da timbrature' : 'stimato',
    ]);
  }

  const kmValido =
    r.km_percorsi !== null && r.km_percorsi !== undefined && r.km_percorsi !== '' && Number(r.km_percorsi) > 0;
  if (kmValido) {
    righeViaggio.push([
      'Km percorsi (andata + ritorno)',
      '',
      '',
      `${Number(r.km_percorsi).toFixed(1).replace('.', ',')} km`,
      '',
    ]);
  }

  if (righeViaggio.length > 0) {
    y = controllaSpazio(doc, y, 20);
    y = titoloSezione(doc, 'Viaggio', y);
    autoTable(
      doc,
      opzioniTabella({
        startY: y,
        head: [['Tratta', 'Partenza', 'Arrivo', 'Durata', 'Origine']],
        body: righeViaggio,
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 25, halign: 'center' },
          3: { cellWidth: 28, halign: 'center' },
          4: { cellWidth: 'auto', fontStyle: 'italic', textColor: GRIGIO_SCURO },
        },
        didParseCell: (data) => {
          // la riga dei km non ha orari: la evidenzio come riepilogo
          if (data.section === 'body' && kmValido && data.row.index === righeViaggio.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [245, 245, 245];
          }
        },
      })
    );
    y = yDopoTabella(doc, y);
  }

  // --- Tabella Ore per operatore ---
  const orePerTecnico = Array.isArray(r.ore_per_tecnico) ? r.ore_per_tecnico : [];
  if (orePerTecnico.length > 0) {
    const righeOperatori = orePerTecnico.map((t) => {
      const op = t || {};
      const note = [];
      // Entrata individuale: chi entra a un orario diverso dalla squadra. Il dato
      // era già calcolato dal backend ma non veniva mai stampato.
      if (op.entrata) {
        note.push(`Entrata ${formatOra(op.entrata)}`);
      }
      if (op.uscita_anticipata) {
        // uscita_anticipata può essere un orario "HH:MM:SS" oppure un flag booleano
        note.push(
          typeof op.uscita_anticipata === 'string'
            ? `Uscita anticipata ${formatOra(op.uscita_anticipata)}`
            : 'Uscita anticipata'
        );
      }
      return [safe(op.nome), formatOre(op.ore), note.join(' · ')];
    });

    y = controllaSpazio(doc, y, 20);
    y = titoloSezione(doc, 'Ore per operatore', y);
    autoTable(
      doc,
      opzioniTabella({
        startY: y,
        head: [['Operatore', 'Ore', 'Note']],
        body: righeOperatori,
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 30 },
          2: { cellWidth: 'auto' },
        },
      })
    );
    y = yDopoTabella(doc, y);
    if (pausaTesto) {
      doc.setTextColor(...GRIGIO_SCURO);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.text(`Le ore indicate sono al netto della pausa di ${pausaTesto}.`, MARGINE, y);
      y += 6;
    }
  }

  // --- Tabella Attività/Task svolte ---
  const tasks = Array.isArray(r.tasks_svolte) ? r.tasks_svolte : [];
  if (tasks.length > 0) {
    const righeTask = tasks.map((task) => {
      const t = task || {};
      // I campi quantità sono variabili: uso il più specifico disponibile.
      let quantita = '';
      if (t.quantita_oggi !== undefined && t.quantita_oggi !== null) {
        quantita = String(t.quantita_oggi);
      } else if (
        t.quantita_completata !== undefined &&
        t.quantita_completata !== null
      ) {
        quantita = String(t.quantita_completata);
      } else if (t.quantita !== undefined && t.quantita !== null) {
        quantita = String(t.quantita);
      }
      return [safe(t.descrizione), quantita || '-'];
    });

    y = controllaSpazio(doc, y, 20);
    y = titoloSezione(doc, 'Attività / Task svolte', y);
    autoTable(
      doc,
      opzioniTabella({
        startY: y,
        head: [['Descrizione', 'Quantità']],
        body: righeTask,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 35, halign: 'center' },
        },
      })
    );
    y = yDopoTabella(doc, y);
  }

  // --- Sezioni di testo libero ---
  y = sezioneTesto(doc, 'Descrizione intervento', r.descrizione_intervento, y);
  y = sezioneTesto(doc, 'Materiali utilizzati', r.materiali_utilizzati, y);
  y = sezioneTesto(doc, 'Note', r.note, y);

  // (i km percorsi sono ora nella tabella "Viaggio" qui sopra, insieme ai tempi)

  // --- Firma cliente ---
  if (r.firma_cliente) {
    // Mi assicuro che ci sia spazio per il blocco firma (immagine 60x30 + testi)
    y = controllaSpazio(doc, y, 50);
    y = titoloSezione(doc, 'Firma cliente', y);

    try {
      // Riquadro bianco con bordo per la firma
      const larghezzaFirma = 60;
      const altezzaFirma = 30;
      doc.setDrawColor(...GRIGIO_CHIARO);
      doc.setLineWidth(0.3);
      doc.rect(MARGINE, y, larghezzaFirma, altezzaFirma);
      // Aggiungo l'immagine della firma (dataURL PNG)
      doc.addImage(
        r.firma_cliente,
        'PNG',
        MARGINE + 1,
        y + 1,
        larghezzaFirma - 2,
        altezzaFirma - 2
      );
      y += altezzaFirma + 5;
    } catch (e) {
      // Se l'immagine non è valida, scrivo un avviso testuale e proseguo.
      // eslint-disable-next-line no-console
      console.error('Impossibile inserire la firma nel PDF:', e);
      doc.setTextColor(...GRIGIO_SCURO);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.text('(firma non disponibile)', MARGINE, y + 5);
      y += 10;
    }

    // Nome del firmatario sotto la firma
    if (r.nome_firmatario) {
      doc.setTextColor(...NERO);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Firmato da: ${r.nome_firmatario}`, MARGINE, y);
      y += 6;
    }
  }

  // --- Footer su tutte le pagine ---
  aggiungiFooter(doc, false);

  // --- Salvataggio ---
  const nomeFile = `rapportino_${sanitizeNomeFile(cliente)}_${sanitizeNomeFile(
    dataRapportino
  )}.pdf`;
  doc.save(nomeFile);
}

// ---------------------------------------------------------------------------
// 2) PDF di riepilogo cantiere
// ---------------------------------------------------------------------------

/**
 * Genera e scarica il PDF (A4 verticale) con il riepilogo di un cantiere,
 * aggregando i dati di tutti i rapportini passati.
 * @param {Object} cantiere - { nome, codice, indirizzo, citta, cliente_nome }.
 * @param {Array}  rapportini - array di rapportini (stessa struttura del singolo).
 * @param {Object} opts - { dataInizio, dataFine } opzionali.
 */
export function generaRiepilogoCantierePDF(cantiere, rapportini, opts) {
  const c = cantiere || {};
  const lista = Array.isArray(rapportini) ? rapportini : [];
  const opzioni = opts || {};

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // --- Intestazione ---
  let y = disegnaIntestazione(doc, 'Riepilogo Cantiere', null);

  // --- Riquadro info cantiere ---
  const indirizzoCompleto = [c.indirizzo, c.citta]
    .filter((v) => v && String(v).trim() !== '')
    .join(', ');

  const bodyInfo = [
    ['Cantiere', safe(c.nome)],
    ['Codice', safe(c.codice)],
    ['Cliente', safe(c.cliente_nome)],
  ];
  if (indirizzoCompleto) {
    bodyInfo.push(['Indirizzo', indirizzoCompleto]);
  }
  // Periodo (se presente almeno una delle due date)
  if (opzioni.dataInizio || opzioni.dataFine) {
    const periodo = `${formatData(opzioni.dataInizio)} - ${formatData(
      opzioni.dataFine
    )}`;
    bodyInfo.push(['Periodo', periodo]);
  }

  autoTable(
    doc,
    opzioniTabella({
      startY: y,
      body: bodyInfo,
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45, fillColor: GRIGIO_CHIARO },
        1: { cellWidth: 'auto' },
      },
      showHead: 'never',
    })
  );
  y = yDopoTabella(doc, y);

  // --- Riepilogo ore (totali + numero rapportini) ---
  const oreTotali = lista.reduce((acc, r) => {
    const ore = Number(r && r.ore_lavorate);
    return acc + (isFinite(ore) && !isNaN(ore) ? ore : 0);
  }, 0);

  y = controllaSpazio(doc, y, 20);
  y = titoloSezione(doc, 'Riepilogo ore', y);
  autoTable(
    doc,
    opzioniTabella({
      startY: y,
      body: [
        ['Ore totali', formatOre(oreTotali)],
        ['Numero rapportini', String(lista.length)],
      ],
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60, fillColor: GRIGIO_CHIARO },
        1: { cellWidth: 'auto' },
      },
      showHead: 'never',
    })
  );
  y = yDopoTabella(doc, y);

  // --- Riepilogo ore per dipendente (aggregazione su tutti i rapportini) ---
  // Mappa: nome operatore -> ore totali sommate.
  const orePerDipendente = {};
  lista.forEach((r) => {
    const tecnici = Array.isArray(r && r.ore_per_tecnico)
      ? r.ore_per_tecnico
      : [];
    tecnici.forEach((t) => {
      if (!t) return;
      const nome = t.nome || `Operatore #${t.user_id != null ? t.user_id : '?'}`;
      const ore = Number(t.ore);
      const oreValide = isFinite(ore) && !isNaN(ore) ? ore : 0;
      orePerDipendente[nome] = (orePerDipendente[nome] || 0) + oreValide;
    });
  });

  const righeDipendenti = Object.keys(orePerDipendente)
    .sort((a, b) => orePerDipendente[b] - orePerDipendente[a]) // ordine decrescente per ore
    .map((nome) => [nome, formatOre(orePerDipendente[nome])]);

  if (righeDipendenti.length > 0) {
    y = controllaSpazio(doc, y, 20);
    y = titoloSezione(doc, 'Riepilogo ore per dipendente', y);
    autoTable(
      doc,
      opzioniTabella({
        startY: y,
        head: [['Operatore', 'Ore totali']],
        body: righeDipendenti,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 40, halign: 'right' },
        },
      })
    );
    y = yDopoTabella(doc, y);
  }

  // --- Riepilogo Task (solo se almeno un rapportino ha task) ---
  // Mappa: descrizione task -> quantità totale svolta.
  const quantitaPerTask = {};
  let almenoUnTask = false;
  lista.forEach((r) => {
    const tasks = Array.isArray(r && r.tasks_svolte) ? r.tasks_svolte : [];
    tasks.forEach((task) => {
      if (!task) return;
      almenoUnTask = true;
      const descrizione = task.descrizione || '(senza descrizione)';
      // Scelgo la quantità "svolta" più specifica disponibile.
      let quantita = 0;
      if (task.quantita_oggi !== undefined && task.quantita_oggi !== null) {
        quantita = Number(task.quantita_oggi);
      } else if (
        task.quantita_completata !== undefined &&
        task.quantita_completata !== null
      ) {
        quantita = Number(task.quantita_completata);
      } else if (task.quantita !== undefined && task.quantita !== null) {
        quantita = Number(task.quantita);
      }
      const qValida = isFinite(quantita) && !isNaN(quantita) ? quantita : 0;
      quantitaPerTask[descrizione] =
        (quantitaPerTask[descrizione] || 0) + qValida;
    });
  });

  if (almenoUnTask && Object.keys(quantitaPerTask).length > 0) {
    const righeTask = Object.keys(quantitaPerTask)
      .sort()
      .map((descrizione) => {
        const q = quantitaPerTask[descrizione];
        // Mostro l'intero così com'è; i decimali con max 2 cifre per leggibilità.
        const qVisibile = Number.isInteger(q)
          ? String(q)
          : String(Math.round(q * 100) / 100);
        return [descrizione, qVisibile];
      });

    y = controllaSpazio(doc, y, 20);
    y = titoloSezione(doc, 'Riepilogo Task', y);
    autoTable(
      doc,
      opzioniTabella({
        startY: y,
        head: [['Task', 'Quantità totale']],
        body: righeTask,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 40, halign: 'center' },
        },
      })
    );
    y = yDopoTabella(doc, y);
  }

  // --- Elenco rapportini ---
  if (lista.length > 0) {
    const righeElenco = lista.map((r) => {
      const rap = r || {};
      const dataRap = rap.data || (rap.intervento && rap.intervento.data);
      const tipologia =
        rap.tipologia || (rap.intervento && rap.intervento.tipologia);
      const firmato = rap.firma_cliente ? 'Sì' : 'No';
      return [
        formatData(dataRap),
        formatTipologia(tipologia),
        formatOre(rap.ore_lavorate),
        safe(rap.creatore_nome),
        firmato,
      ];
    });

    y = controllaSpazio(doc, y, 20);
    y = titoloSezione(doc, 'Elenco rapportini', y);
    autoTable(
      doc,
      opzioniTabella({
        startY: y,
        head: [['Data', 'Tipologia', 'Ore', 'Compilato da', 'Firmato']],
        body: righeElenco,
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 32 },
          2: { cellWidth: 25, halign: 'right' },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 22, halign: 'center' },
        },
      })
    );
    y = yDopoTabella(doc, y);
  } else {
    // Nessun rapportino: messaggio informativo.
    y = controllaSpazio(doc, y, 12);
    doc.setTextColor(...GRIGIO_SCURO);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text('Nessun rapportino disponibile per questo cantiere.', MARGINE, y);
    y += 8;
  }

  // --- Footer con numero pagina e data di generazione ---
  aggiungiFooter(doc, true);

  // --- Salvataggio ---
  const nomeFile = `riepilogo_${sanitizeNomeFile(c.nome)}.pdf`;
  doc.save(nomeFile);
}

// ---------------------------------------------------------------------------
// 3) Report mensile del cantiere: ore per persona, giorno per giorno
// ---------------------------------------------------------------------------

const MESI = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];
const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];

/** Nome del giorno della settimana da "YYYY-MM-DD", o "" se non interpretabile. */
function nomeGiorno(data) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(data || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? '' : GIORNI[d.getDay()];
}

/** "2026-08" -> "agosto 2026" */
export function etichettaMese(meseIso) {
  const m = /^(\d{4})-(\d{2})/.exec(String(meseIso || ''));
  if (!m) return String(meseIso || '');
  return `${MESI[Number(m[2]) - 1]} ${m[1]}`;
}

/**
 * Genera il PDF del report mensile di un cantiere.
 * @param {Object} cantiere - { nome, codice, cliente_nome, indirizzo, citta }
 * @param {string} meseIso  - "YYYY-MM"
 * @param {Array}  persone  - [{ nome, giorni: [{data, ore, note}], totaleOre, giorniLavorati }]
 * @param {Object} opts     - { rapportiniSenzaOre: number }
 */
export function generaReportMensileCantierePDF(cantiere, meseIso, persone, opts) {
  const c = cantiere || {};
  const elenco = Array.isArray(persone) ? persone : [];
  const o = opts || {};

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = disegnaIntestazione(doc, 'Report mensile', etichettaMese(meseIso));

  // --- Riquadro informazioni ---
  const righeInfo = [];
  if (c.cliente_nome) righeInfo.push(['Cliente', safe(c.cliente_nome)]);
  if (c.codice) righeInfo.push(['Commessa', safe(c.codice)]);
  righeInfo.push(['Cantiere', safe(c.nome)]);
  if (c.indirizzo || c.citta) {
    righeInfo.push(['Indirizzo', [c.indirizzo, c.citta].filter(Boolean).join(', ')]);
  }
  righeInfo.push(['Periodo', etichettaMese(meseIso)]);

  autoTable(
    doc,
    opzioniTabella({
      startY: y,
      body: righeInfo,
      showHead: 'never',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 32, fillColor: GRIGIO_CHIARO },
        1: { cellWidth: 'auto' },
      },
    })
  );
  y = yDopoTabella(doc, y);

  if (elenco.length === 0) {
    y = controllaSpazio(doc, y, 16);
    doc.setTextColor(...GRIGIO_SCURO);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text('Nessuna ora registrata su questo cantiere nel mese selezionato.', MARGINE, y);
    aggiungiFooter(doc, true);
    doc.save(`report_${sanitizeNomeFile(c.codice || c.nome)}_${meseIso}.pdf`);
    return;
  }

  // --- Riepilogo complessivo, in cima: è il numero che si guarda per primo ---
  const totaleOre = elenco.reduce((acc, p) => acc + (p.totaleOre || 0), 0);
  y = controllaSpazio(doc, y, 20);
  y = titoloSezione(doc, 'Riepilogo del mese', y);
  autoTable(
    doc,
    opzioniTabella({
      startY: y,
      head: [['Persona', 'Giorni', 'Ore']],
      body: [
        ...elenco.map((p) => [safe(p.nome), String(p.giorniLavorati || 0), formatOre(p.totaleOre)]),
        ['TOTALE', String(elenco.reduce((a, p) => a + (p.giorniLavorati || 0), 0)), formatOre(totaleOre)],
      ],
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 30, halign: 'right' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === elenco.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [255, 230, 230];
        }
      },
    })
  );
  y = yDopoTabella(doc, y);

  // --- Dettaglio giorno per giorno, una sezione per persona ---
  elenco.forEach((p) => {
    y = controllaSpazio(doc, y, 30);
    y = titoloSezione(doc, safe(p.nome), y);

    const righe = (p.giorni || []).map((g) => [
      formatData(g.data),
      nomeGiorno(g.data),
      formatOre(g.ore),
      g.note || '',
    ]);
    const n = p.giorniLavorati || 0;
    righe.push(['', 'Totale', formatOre(p.totaleOre), `${n} ${n === 1 ? 'giorno' : 'giorni'}`]);

    autoTable(
      doc,
      opzioniTabella({
        startY: y,
        head: [['Data', 'Giorno', 'Ore', 'Note']],
        body: righe,
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 28 },
          2: { cellWidth: 25, halign: 'right' },
          3: { cellWidth: 'auto', fontSize: 8, textColor: GRIGIO_SCURO },
        },
        didParseCell: (data) => {
          if (data.section === 'body' && data.row.index === righe.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = GRIGIO_CHIARO;
          }
        },
      })
    );
    y = yDopoTabella(doc, y);
  });

  // Se dei rapportini non hanno le ore per persona il totale è parziale: va detto,
  // altrimenti il documento sembra completo quando non lo è.
  if (o.rapportiniSenzaOre > 0) {
    y = controllaSpazio(doc, y, 12);
    doc.setTextColor(...GRIGIO_SCURO);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.text(
      `Nota: ${o.rapportiniSenzaOre} rapportino/i del periodo non riporta le ore per persona e non è conteggiato qui sopra.`,
      MARGINE,
      y
    );
    y += 6;
  }

  aggiungiFooter(doc, true);
  doc.save(`report_${sanitizeNomeFile(c.codice || c.nome)}_${meseIso}.pdf`);
}
